// src/lib/pdfExport.js
// ─────────────────────────────────────────────────────────────
// Export přihlášky / souhrnného dokumentu do PDF
// Používá jsPDF + jsPDF-AutoTable + GT America (UTF-8 / čeština)
// ─────────────────────────────────────────────────────────────

import jsPDF from 'jspdf';
import 'jspdf-autotable';

import GTAmericaRegularUrl from '../fonts/GT-America-Standard-Regular.ttf';
import GTAmericaBoldUrl    from '../fonts/GT-America-Standard-Bold.ttf';

const JIC_PURPLE = [91, 20, 179];
const JIC_YELLOW = [254, 216, 117];
const JIC_DARK   = [26, 26, 26];
const JIC_GRAY   = [120, 120, 120];
const JIC_LIGHT  = [245, 243, 239];

// ── Načtení fontu jako base64 (pro jsPDF) ────────────────────
async function loadFontBase64(url) {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// ── Vytvoření doc s embedded GT America ──────────────────────
async function createDoc(options = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', ...options });

  const [regularB64, boldB64] = await Promise.all([
    loadFontBase64(GTAmericaRegularUrl),
    loadFontBase64(GTAmericaBoldUrl),
  ]);

  doc.addFileToVFS('GTAmerica-Regular.ttf', regularB64);
  doc.addFont('GTAmerica-Regular.ttf', 'GTAmerica', 'normal');
  doc.addFileToVFS('GTAmerica-Bold.ttf', boldB64);
  doc.addFont('GTAmerica-Bold.ttf', 'GTAmerica', 'bold');

  doc.setFont('GTAmerica', 'normal');
  return doc;
}

// ── Helper: záhlaví každé stránky ────────────────────────────
function addHeader(doc, title, pageNum, totalPages) {
  doc.setFillColor(...JIC_PURPLE);
  doc.rect(0, 0, 210, 14, 'F');

  doc.setFont('GTAmerica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text('JIC', 14, 9.5);

  doc.setFont('GTAmerica', 'normal');
  doc.setFontSize(8);
  doc.text('Founder Stipendium 2026', 22, 9.5);

  doc.setFont('GTAmerica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(title, 210 - 14, 9.5, { align: 'right' });

  doc.setFont('GTAmerica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...JIC_GRAY);
  doc.text(`${pageNum} / ${totalPages}`, 210 - 14, 292, { align: 'right' });
}

// ── Vloží obsah jedné přihlášky do existujícího doc ──────────
function appendAppToDoc(doc, app) {
  const name = app.name || 'Přihláška';

  const questions = [
    ['Q1',  'Jméno a příjmení',             app.name],
    ['Q2',  'E-mail',                        app.email],
    ['Q3',  'Telefon',                       app.phone],
    ['Q4',  'Absolvované studium',           app.edu],
    ['Q5',  'Pracovní závazky',              app.jobs],
    ['Q6',  'Proč startup & stipendium?',    app.motiv],
    ['Q7',  'Popis nápadu',                  app.idea],
    ['Q8',  'Oblast uplatnění',              app.area],
    ['Q9',  'Inovativnost produktu',         app.uniq],
    ['Q10', 'Zájem o produkt',               app.demand],
    ['Q11', 'Aktuální fáze',                 app.phase],
    ['Q12', 'Cíle na 3 měsíce',             app.goals],
    ['Q13', 'Rizika projektu',               app.risks],
    ['Q14', 'Tým projektu',                  app.team],
    ['Q15', 'Přístrojové vybavení',          app.equip],
    ['Q16', 'Odkaz na video',                app.video],
    ['Q17', 'Doklad o vzdělání (soubor)',    app.file17_name],
    ['Q18', 'Další dokument (soubor)',       app.file18_name],
  ];

  // Titulní blok
  doc.setFillColor(...JIC_LIGHT);
  doc.rect(0, 14, 210, 42, 'F');

  doc.setFont('GTAmerica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...JIC_DARK);
  doc.text('Přihláška', 14, 30);

  doc.setFont('GTAmerica', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(...JIC_PURPLE);
  doc.text('Founder Stipendium 2026', 14, 39);

  doc.setFont('GTAmerica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...JIC_GRAY);
  const submittedAt = app.submitted_at
    ? new Date(app.submitted_at).toLocaleString('cs-CZ')
    : 'Neodesláno';
  doc.text(`Odesláno: ${submittedAt}`, 14, 50);
  doc.text(`ID: ${app.session_id || app.id || '—'}`, 14, 56);

  // Tabulka otázek
  doc.autoTable({
    startY: 62,
    head: [['Otázka', 'Odpověď']],
    body: questions.map(([q, label, val]) => [
      `${q}  ${label}`,
      val || '—',
    ]),
    styles: {
      font: 'GTAmerica',
      fontSize: 9,
      cellPadding: { top: 5, right: 8, bottom: 5, left: 8 },
      textColor: JIC_DARK,
      lineColor: [230, 228, 220],
      lineWidth: 0.3,
    },
    headStyles: {
      fillColor: JIC_PURPLE,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    alternateRowStyles: { fillColor: JIC_LIGHT },
    columnStyles: {
      0: { cellWidth: 55, fontStyle: 'bold', textColor: JIC_GRAY },
      1: { cellWidth: 'auto' },
    },
    margin: { left: 14, right: 14 },
    didDrawPage: (data) => {
      addHeader(doc, name, data.pageNumber, '?');
    },
  });

  // Přepíše záhlaví se správným celkovým počtem stránek
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addHeader(doc, name, i, pageCount);
  }
}

// ── Export jedné přihlášky ────────────────────────────────────
export async function exportApplicationPDF(app) {
  const doc = await createDoc();
  appendAppToDoc(doc, app);
  const name = app.name || 'Přihláška';
  doc.save(`JIC_Founder_${name.replace(/\s/g, '_')}.pdf`);
}

// ── Export souhrnného dokumentu (všechny přihlášky) ───────────
export async function exportSummaryPDF(apps) {
  const doc = await createDoc({ orientation: 'landscape' });

  // Titulní strana
  doc.setFillColor(...JIC_PURPLE);
  doc.rect(0, 0, 297, 210, 'F');

  doc.setFont('GTAmerica', 'bold');
  doc.setFontSize(36);
  doc.setTextColor(255, 255, 255);
  doc.text('Founder Stipendium', 148.5, 85, { align: 'center' });

  doc.setFont('GTAmerica', 'normal');
  doc.setFontSize(18);
  doc.setTextColor(...JIC_YELLOW);
  doc.text('Přehled všech přihlášek 2026', 148.5, 100, { align: 'center' });

  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text(`Celkem přihlášek: ${apps.length}`, 148.5, 118, { align: 'center' });
  doc.text(`Exportováno: ${new Date().toLocaleString('cs-CZ')}`, 148.5, 126, { align: 'center' });

  doc.setFontSize(9);
  doc.setTextColor(...JIC_YELLOW);
  doc.text('JIC – Jihomoravské inovační centrum', 148.5, 190, { align: 'center' });

  // Souhrnná tabulka
  doc.addPage('landscape');

  doc.autoTable({
    startY: 20,
    head: [['#', 'Jméno', 'E-mail', 'Telefon', 'Startup nápad', 'Fáze', 'Stav', 'Odesláno']],
    body: apps.map((a, i) => [
      i + 1,
      a.name || '—',
      a.email || '—',
      a.phone || '—',
      (a.idea || '—').slice(0, 80) + ((a.idea || '').length > 80 ? '…' : ''),
      (a.phase || '—').slice(0, 40) + ((a.phase || '').length > 40 ? '…' : ''),
      a.status === 'done' ? 'Odesláno' : a.status === 'partial' ? 'Rozepsáno' : 'Draft',
      a.submitted_at ? new Date(a.submitted_at).toLocaleDateString('cs-CZ') : '—',
    ]),
    styles: {
      font: 'GTAmerica',
      fontSize: 8,
      cellPadding: { top: 4, right: 6, bottom: 4, left: 6 },
      textColor: JIC_DARK,
      lineColor: [230, 228, 220],
      lineWidth: 0.3,
    },
    headStyles: {
      fillColor: JIC_DARK,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: JIC_LIGHT },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 38, fontStyle: 'bold' },
      2: { cellWidth: 48 },
      3: { cellWidth: 28 },
      4: { cellWidth: 70 },
      5: { cellWidth: 45 },
      6: { cellWidth: 22, halign: 'center' },
      7: { cellWidth: 22, halign: 'center' },
    },
    margin: { left: 10, right: 10, top: 20 },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) addHeader(doc, 'Souhrnný přehled', data.pageNumber, '?');
    },
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 2; i <= pageCount; i++) {
    doc.setPage(i);
    addHeader(doc, 'Souhrnný přehled', i, pageCount);
  }

  doc.save(`JIC_FounderStipendia_Souhrn_${new Date().toISOString().slice(0, 10)}.pdf`);
}
